/**
 * Permission constants that EXACTLY mirror the backend definitions
 * Source: optimo_core/permissions/definitions.py
 *
 * IMPORTANT: These constants MUST stay synchronized with backend
 * Any changes to backend permissions require updating this file
 */

// Permission definition type matching backend Permission namedtuple
export interface Permission {
    readonly code: string
    readonly label: string
}

// Create permission helper function
const createPermission = (code: string, label: string): Permission =>
    ({
        code,
        label,
    }) as const

export const PERMISSIONS = {
    // Employee Management Permissions - Mirror of Permissions.Employee
    EMPLOYEE: {
        VIEW: createPermission('employee.view', 'View Employee Records'),
        VIEW_SELF: createPermission('employee.view.self', 'View Own Employee Profile'),
        VIEW_TEAM: createPermission('employee.view.team', 'View Direct Reports'),
        VIEW_DEPARTMENT: createPermission('employee.view.department', 'View Department Employees'),
        VIEW_ALL: createPermission('employee.view.all', 'View All Employees'),
        CREATE: createPermission('employee.create', 'Create New Employees'),
        EDIT: createPermission('employee.edit', 'Edit Employee Data'),
        EDIT_SELF: createPermission('employee.edit.self', 'Edit Own Profile'),
        EDIT_TEAM: createPermission('employee.edit.team', 'Edit Direct Reports'),
        DELETE: createPermission('employee.delete', 'Delete Employees'),
        EXPORT: createPermission('employee.export', 'Export Employee Data'),
        VIEW_COMPENSATION: createPermission('employee.view.compensation', 'View Salary & Compensation'),
        VIEW_PERSONAL: createPermission('employee.view.personal', 'View Personal Information (SSN, etc.)'),
        VIEW_PERFORMANCE: createPermission('employee.view.performance', 'View Performance Reviews'),
        EDIT_COMPENSATION: createPermission('employee.edit.compensation', 'Edit Compensation'),
        MANAGE_LIFECYCLE: createPermission('employee.manage.lifecycle', 'Manage Onboarding/Offboarding'),
        VIEW_REPORTS: createPermission('employee.view.reports', 'View Employee Reports'),
        MANAGE_DOCUMENTS: createPermission('employee.manage.documents', 'Manage Employee Documents'),
        VIEW_DIRECTORY: createPermission('employee.view.directory', 'View Employee Directory'),
    },

    // HRIS Import Permissions - Mirror of Permissions.HRISImport
    HRIS_IMPORT: {
        UPLOAD: createPermission('hris_import.upload', 'Upload HRIS Employee Data Files'),
        VIEW_JOBS: createPermission('hris_import.view_jobs', 'View HRIS Import Jobs'),
        VIEW_MAPPINGS: createPermission('hris_import.view_mappings', 'View Field Mappings'),
        EDIT_MAPPINGS: createPermission('hris_import.edit_mappings', 'Edit Field Mappings'),
        CONFIRM_MAPPINGS: createPermission('hris_import.confirm_mappings', 'Confirm & Process HRIS Imports'),
        VIEW_RESULTS: createPermission('hris_import.view_results', 'View Import Results'),
        DOWNLOAD_ERRORS: createPermission('hris_import.download_errors', 'Download Error Reports'),
        RETRY_FAILED: createPermission('hris_import.retry_failed', 'Retry Failed Imports'),
        VIEW_AUDIT_TRAIL: createPermission('hris_import.view_audit_trail', 'View HRIS Import Audit Trails'),
    },

    // Survey Management Permissions - Mirror of Permissions.Surveys
    SURVEYS: {
        VIEW: createPermission('surveys.view', 'View Surveys'),
        CREATE: createPermission('surveys.create', 'Create New Surveys'),
        EDIT: createPermission('surveys.edit', 'Edit Surveys'),
        DELETE: createPermission('surveys.delete', 'Delete Surveys'),
        ACTIVATE: createPermission('surveys.activate', 'Activate/Deactivate Surveys'),
        VIEW_RESPONSES: createPermission('surveys.view_responses', 'View Survey Responses'),
        ANALYZE: createPermission('surveys.analyze', 'Access Survey Analytics'),
        EXPORT: createPermission('surveys.export', 'Export Survey Data'),
        RESPOND: createPermission('surveys.respond', 'Submit Survey Responses'),
        VIEW_OWN: createPermission('surveys.view_own', 'View Own Survey Responses'),
        EXIT_MANAGE: createPermission('surveys.exit.manage', 'Manage Exit Surveys'),
        ONBOARDING_MANAGE: createPermission('surveys.onboarding.manage', 'Manage Onboarding Surveys'),
        TEMPLATES_MANAGE: createPermission('surveys.templates.manage', 'Manage Survey Templates'),
        ANONYMOUS_PROTECT: createPermission('surveys.anonymous.protect', 'Ensure Response Anonymity'),
    },

    // Integration Permissions - Mirror of Permissions.Integrations
    INTEGRATIONS: {
        SLACK_MANAGE: createPermission('integrations.slack.manage', 'Configure Slack Integration'),
        SLACK_SEND: createPermission('integrations.slack.send', 'Send Messages via Slack'),
        SLACK_SURVEYS: createPermission('integrations.slack.surveys', 'Send Surveys via Slack'),
        TEAMS_MANAGE: createPermission('integrations.teams.manage', 'Configure Teams Integration'),
        TEAMS_SEND: createPermission('integrations.teams.send', 'Send Messages via Teams'),
        ADMIN_INSTALL: createPermission('integrations.admin.install', 'Install New Integrations'),
        ADMIN_UNINSTALL: createPermission('integrations.admin.uninstall', 'Remove Integrations'),
    },

    // Kudos Permissions - Mirror of Permissions.Kudos
    KUDOS: {
        SEND: createPermission('kudos.send', 'Send Kudos'),
        VIEW: createPermission('kudos.view', 'View Kudos'),
        MANAGE: createPermission('kudos.manage', 'Manage Kudos Categories'),
        EXPORT: createPermission('kudos.export', 'Export Kudos Data'),
    },

    // Analytics Permissions - Mirror of Permissions.Analytics
    ANALYTICS: {
        VIEW_OVERVIEW: createPermission('analytics.view.overview', 'View Analytics Dashboard'),
        VIEW_DEMOGRAPHICS: createPermission('analytics.view.demographics', 'View Demographics Data'),
        VIEW_ENGAGEMENT: createPermission('analytics.view.engagement', 'View Engagement Metrics'),
        VIEW_RETENTION: createPermission('analytics.view.retention', 'View Retention Analytics'),
        VIEW_COMPENSATION: createPermission('analytics.view.compensation', 'View Compensation Analytics'),
        VIEW_TEAM: createPermission('analytics.view.team', 'View Team Analytics'),
        EXPORT: createPermission('analytics.export', 'Export Analytics Reports'),
        VIEW_BENCHMARKS: createPermission('analytics.view.benchmarks', 'View Industry Benchmarks'),
        CREATE_REPORTS: createPermission('analytics.create.reports', 'Create Custom Reports'),
        SCHEDULE_REPORTS: createPermission('analytics.schedule.reports', 'Schedule Automated Reports'),
    },

    // User Management Permissions - Mirror of Permissions.Users
    USERS: {
        VIEW: createPermission('users.view', 'View User Accounts'),
        SEARCH: createPermission('users.search', 'Search Users'),
        CREATE: createPermission('users.create', 'Create User Accounts'),
        EDIT: createPermission('users.edit', 'Edit User Accounts'),
        DELETE: createPermission('users.delete', 'Delete User Accounts'),
        RESET_PASSWORD: createPermission('users.reset_password', 'Reset User Passwords'),
        MANAGE_MFA: createPermission('users.manage_mfa', 'Manage Multi-Factor Authentication'),
        MANAGE_SESSIONS: createPermission('users.manage_sessions', 'Manage Active Sessions'),
    },

    // Admin Permissions - Mirror of Permissions.Admin
    ADMIN: {
        VIEW_SETTINGS: createPermission('admin.view_settings', 'View Organization Settings'),
        EDIT_SETTINGS: createPermission('admin.edit_settings', 'Edit Organization Settings'),
        VIEW_AUDIT_LOGS: createPermission('admin.view_audit_logs', 'View Audit Logs'),
        EXPORT_AUDIT_LOGS: createPermission('admin.export_audit_logs', 'Export Audit Logs'),
        MANAGE_API_KEYS: createPermission('admin.manage_api_keys', 'Manage API Keys'),
        MANAGE_SECURITY: createPermission('admin.manage_security', 'Manage Security Settings'),
        MANAGE_ROLES: createPermission('admin.manage_roles', 'Manage User Roles'),
        MANAGE_PERMISSIONS: createPermission('admin.manage_permissions', 'Manage Permissions'),
        VIEW_METRICS: createPermission('admin.view_metrics', 'View System Metrics'),
        MANAGE_BACKUPS: createPermission('admin.manage_backups', 'Manage System Backups'),
        TRANSFER_OWNERSHIP: createPermission('admin.transfer_ownership', 'Transfer Organization Ownership'),
        DELETE_ORGANIZATION: createPermission('admin.delete_organization', 'Delete Organization'),
    },

    // HR Intake Configuration Permissions - Mirror of Permissions.HRIntake
    HR_INTAKE: {
        VIEW: createPermission('hr_intake.view', 'View HR Intake Configuration'),
        CREATE: createPermission('hr_intake.create', 'Create HR Intake Configuration'),
        UPDATE: createPermission('hr_intake.update', 'Update HR Intake Configuration'),
        DELETE: createPermission('hr_intake.delete', 'Delete HR Intake Configuration'),
    },

    // Impersonation Permissions - Mirror of Permissions.Impersonation
    IMPERSONATION: {
        START: createPermission('impersonation.start', 'Start Impersonation Session'),
        END: createPermission('impersonation.end', 'End Impersonation Session'),
        VIEW_ACTIVE: createPermission('impersonation.view_active', 'View Active Impersonation Sessions'),
        TARGET_EMPLOYEE: createPermission('impersonation.target.employee', 'Impersonate Employees'),
        TARGET_MANAGER: createPermission('impersonation.target.manager', 'Impersonate Managers'),
        TARGET_HR: createPermission('impersonation.target.hr', 'Impersonate HR Staff'),
        TARGET_ADMIN: createPermission('impersonation.target.admin', 'Impersonate Administrators'),
        SCOPE_READ_ONLY: createPermission('impersonation.scope.read_only', 'Read-Only Impersonation'),
        SCOPE_WRITE_LIMITED: createPermission('impersonation.scope.write_limited', 'Limited Write Impersonation'),
        SCOPE_FULL_ACCESS: createPermission('impersonation.scope.full_access', 'Full Access Impersonation'),
        MANAGE_ALL: createPermission('impersonation.manage_all', 'Manage All Impersonation Sessions'),
    },
} as const

// Helper type to extract all permission codes
export type PermissionCode =
    (typeof PERMISSIONS)[keyof typeof PERMISSIONS][keyof (typeof PERMISSIONS)[keyof typeof PERMISSIONS]]['code']

// Helper functions for working with permissions
export const getAllPermissionCodes = (): string[] => {
    return Object.values(PERMISSIONS)
        .flatMap((category) => Object.values(category))
        .map((permission) => permission.code)
}

export const getPermissionsByCategory = (category: keyof typeof PERMISSIONS): Permission[] => {
    return Object.values(PERMISSIONS[category])
}

export const findPermissionByCode = (code: string): Permission | undefined => {
    return getAllPermissions().find((permission) => permission.code === code)
}

export const getAllPermissions = (): Permission[] => {
    return Object.values(PERMISSIONS).flatMap((category) => Object.values(category))
}

// Validate permission code format
export const isValidPermissionCode = (code: string): code is PermissionCode => {
    return getAllPermissionCodes().includes(code)
}

// Helper to get permission label by code
export const getPermissionLabel = (code: string): string => {
    const permission = findPermissionByCode(code)
    return permission?.label || code
}
